import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/formatters";

import type { Aggregation, Metric } from "./types";
import type { SelectionState, Totals } from "./useRangeSelection";

const POPUP_OFFSET_PX = 8;
const POPUP_HALF_WIDTH_PX = 90;

type RangeSelectPopupProps = {
  selection: SelectionState | null;
  totals: Totals | null;
  wrapperWidth: number;
  aggregation: Aggregation;
  metric: Metric;
};

function fmtNumber(n: number): string {
  return formatNumber(n, { maximumFractionDigits: 1 });
}

function fmtDate(ms: number, aggregation: Aggregation): string {
  return aggregation === "quarter"
    ? dayjs(ms).format("MMM YYYY")
    : dayjs(ms).format("MMM D, YYYY");
}

export function RangeSelectPopup({
  selection,
  totals,
  wrapperWidth,
  aggregation,
  metric,
}: RangeSelectPopupProps) {
  const { t } = useTranslation();

  if (!selection || !totals) return null;

  const tilde = totals.isEstimated ? "~ " : "";
  const unit =
    metric === "revenue" ? "" : ` ${t("components.demandChart.popup.units")}`;
  const perUnit =
    metric === "revenue"
      ? `/ ${aggregation}`
      : ` ${t("components.demandChart.popup.units")} / ${aggregation}`;

  const minLeft = POPUP_HALF_WIDTH_PX;
  const maxLeft = Math.max(minLeft, wrapperWidth - POPUP_HALF_WIDTH_PX);
  const left = Math.min(maxLeft, Math.max(minLeft, selection.pixelMidX));

  const caption = totals.forecastOnly
    ? t("components.demandChart.popup.forecastOnly")
    : totals.isEstimated
      ? t("components.demandChart.popup.includesForecast")
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-none absolute z-10 min-w-[160px] -translate-x-1/2 -translate-y-full",
        "rounded-md border border-zinc-300 bg-white/95 px-3 py-2 text-xs shadow-md",
      )}
      style={{
        left,
        top: selection.pixelTopY - POPUP_OFFSET_PX,
      }}
    >
      <div className="text-zinc-500">
        {fmtDate(selection.startMs, aggregation)} –{" "}
        {fmtDate(selection.endMs, aggregation)}
      </div>
      <div className="mt-1 font-medium text-zinc-900">
        {tilde}
        {fmtNumber(totals.total)}
        {unit} {t("components.demandChart.popup.total")}
      </div>
      <div className="text-zinc-600">
        {tilde}
        {fmtNumber(totals.avgPerPeriod)}
        {perUnit}
      </div>
      {caption && (
        <div className="mt-1 text-[10px] tracking-wide text-zinc-400 uppercase">
          {caption}
        </div>
      )}
    </div>
  );
}
